import {
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  ForemanPhase,
} from '@ups/xplat/api/dto';
import type { ForemanJobService } from './foreman-job.service';

/**
 * Work orders whose id is present AND requested.
 *
 * The `!!workOrder.foremanPhaseID` half is load-bearing, not defensive noise:
 * `ForemanPhaseID` is `Guid?` on the wire (#30151), and every consumer below writes
 * the id straight into a `foremanPhaseIDs: string[]`. Without this filter a null-id
 * work order puts a `null` ELEMENT into that array, and downstream code selects rows
 * by those ids. `strictNullChecks` is off workspace-wide, so the compiler cannot
 * catch it, which is exactly why the guard has to be explicit here.
 *
 * The type predicate rather than a plain truthiness filter is what keeps the
 * narrowing through the later `.map`, matching `foreman-work-order-visibility.utils`.
 */
function resolveWorkOrders(
  service: ForemanJobService,
  workOrderIds: string | string[]
) {
  const currentJob = service.selectedJob();
  const ids = Array.isArray(workOrderIds)
    ? workOrderIds
    : [workOrderIds].filter(Boolean);

  return (currentJob?.workOrders ?? []).filter(
    (workOrder): workOrder is ForemanPhase & { foremanPhaseID: string } =>
      !!workOrder.foremanPhaseID && ids.includes(workOrder.foremanPhaseID)
  );
}

function assignWorkOrders(
  entity: {
    foremanPhaseID?: string | null;
    foremanPhaseName?: string | null;
    foremanPhaseIDs?: string[] | null;
    foremanPhaseNames?: string[] | null;
  },
  workOrders: Array<{ foremanPhaseID: string; phaseName?: string | null }>,
  overwriteExisting: boolean
) {
  if (overwriteExisting || !entity.foremanPhaseIDs?.length) {
    entity.foremanPhaseIDs = workOrders.map((w) => w.foremanPhaseID);
    entity.foremanPhaseNames = workOrders.map((w) => w.phaseName);
  } else {
    const existingIds = entity.foremanPhaseIDs || [];
    const existingNameById = new Map(
      existingIds.map((id, index) => [
        id,
        entity.foremanPhaseNames?.[index] ?? null,
      ])
    );

    for (const workOrder of workOrders) {
      existingNameById.set(
        workOrder.foremanPhaseID,
        workOrder.phaseName ?? null
      );
    }

    const newIds = workOrders
      .map((w) => w.foremanPhaseID)
      .filter((id) => !existingIds.includes(id));

    entity.foremanPhaseIDs = [...existingIds, ...newIds];
    entity.foremanPhaseNames = entity.foremanPhaseIDs
      .map((id) => existingNameById.get(id))
      .filter(Boolean);
  }

  entity.foremanPhaseID = entity.foremanPhaseIDs?.[0] || null;
  entity.foremanPhaseName = entity.foremanPhaseNames?.[0] || null;
}

export function setDefaultEmployeeWorkOrders(
  service: ForemanJobService,
  workOrderIds: string | string[],
  overwriteExisting?: boolean
) {
  const currentJob = service.selectedJob();
  if (!currentJob || !currentJob.employees) return;

  const workOrders = resolveWorkOrders(service, workOrderIds);

  for (const employee of currentJob.employees) {
    assignWorkOrders(employee, workOrders, !!overwriteExisting);
  }

  service.selectedJob.set({ ...currentJob });
}

export function setDefaultEquipmentWorkOrders(
  service: ForemanJobService,
  workOrderIds: string | string[],
  overwriteExisting?: boolean
) {
  const currentJob = service.selectedJob();
  if (!currentJob || !currentJob.equipment) return;

  const workOrders = resolveWorkOrders(service, workOrderIds);

  for (const equipment of currentJob.equipment) {
    assignWorkOrders(equipment, workOrders, !!overwriteExisting);
  }

  service.selectedJob.set({ ...currentJob });
}

export function setEquipmentWorkOrder(
  service: ForemanJobService,
  equipment: ForemanEquipmentDispatch,
  workOrderIds: string | string[],
  overwriteExisting = false
) {
  assignWorkOrders(
    equipment,
    resolveWorkOrders(service, workOrderIds),
    overwriteExisting
  );
}

export function setEmployeeWorkOrder(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch,
  workOrderIds: string | string[],
  overwriteExisting = false
) {
  assignWorkOrders(
    employee,
    resolveWorkOrders(service, workOrderIds),
    overwriteExisting
  );
}

export function removeEmployeeWorkOrderSelection(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch,
  workOrderId: string
) {
  const currentJob = service.selectedJob();
  // Same id-presence guard as `resolveWorkOrders`, and it is NOT redundant with it:
  // this function rebuilds `foremanPhaseIDs` from `currentJob.workOrders` directly,
  // so it is a second independent path from the nullable wire contract into a
  // `string[]`. It also has to cope with an array persisted BEFORE that guard existed.
  const remainingWorkOrders = (currentJob?.workOrders ?? []).filter(
    (workOrder): workOrder is ForemanPhase & { foremanPhaseID: string } =>
      !!workOrder.foremanPhaseID &&
      workOrder.foremanPhaseID !== workOrderId &&
      (employee.foremanPhaseIDs || []).includes(workOrder.foremanPhaseID)
  );

  employee.foremanPhaseIDs = remainingWorkOrders.map(
    (workOrder) => workOrder.foremanPhaseID
  );
  employee.foremanPhaseNames = remainingWorkOrders.map(
    (workOrder) => workOrder.phaseName
  );
  employee.foremanPhaseID = employee.foremanPhaseIDs[0] || null;
  employee.foremanPhaseName = employee.foremanPhaseNames[0] || null;
}
